/**
 * Track Image Assets
 *
 * Cover images for educational tracks in QultureUp and TelePortal.
 * Organized by track category and topic.
 */

import type { AssetPath } from '../types';

/**
 * Base path for track images
 */
const TRACKS_BASE = '/assets/images/tracks';

/**
 * Track category types
 */
export type TrackCategory = 'culture' | 'legal' | 'lifestyle' | 'professional' | 'technology';

/**
 * Track definition with metadata
 */
export interface TrackImageDefinition extends AssetPath {
  id: string;
  name: string;
  category: TrackCategory;
}

/**
 * Culture-related tracks
 */
export const CULTURE_TRACKS: TrackImageDefinition[] = [
  {
    id: 'culture-fundamentals',
    name: 'Culture Fundamentals',
    category: 'culture',
    path: `${TRACKS_BASE}/culture-fundamentals.jpg`,
    alt: 'Culture fundamentals track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'customs-traditions',
    name: 'Customs & Traditions',
    category: 'culture',
    path: `${TRACKS_BASE}/customs-traditions.jpg`,
    alt: 'Customs and traditions track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'foods-drinks',
    name: 'Foods & Drinks',
    category: 'culture',
    path: `${TRACKS_BASE}/foods-drinks.jpg`,
    alt: 'Foods and drinks track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'greetings-etiquette',
    name: 'Greetings & Etiquette',
    category: 'culture',
    path: `${TRACKS_BASE}/greetings-etiquette.jpg`,
    alt: 'Greetings and etiquette track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'linguistic-differences',
    name: 'Linguistic Differences',
    category: 'culture',
    path: `${TRACKS_BASE}/linguistic-differences.jpg`,
    alt: 'Linguistic differences track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'geography-tourism',
    name: 'Geography & Tourism',
    category: 'culture',
    path: `${TRACKS_BASE}/geography-tourism.jpg`,
    alt: 'Geography and tourism track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'politics',
    name: 'Politics',
    category: 'culture',
    path: `${TRACKS_BASE}/politics.jpg`,
    alt: 'Politics track cover',
    width: 800,
    height: 600,
  },
];

/**
 * Legal and administrative tracks
 */
export const LEGAL_TRACKS: TrackImageDefinition[] = [
  {
    id: 'immigration-residency',
    name: 'Immigration & Residency',
    category: 'legal',
    path: `${TRACKS_BASE}/immigration-residency.jpg`,
    alt: 'Immigration and residency track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'labor-rights',
    name: 'Labor Rights',
    category: 'legal',
    path: `${TRACKS_BASE}/labor-rights.jpg`,
    alt: 'Labor rights track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'consumer-rights',
    name: 'Consumer Rights',
    category: 'legal',
    path: `${TRACKS_BASE}/consumer-rights.jpg`,
    alt: 'Consumer rights track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'housing-rights',
    name: 'Housing Rights',
    category: 'legal',
    path: `${TRACKS_BASE}/housing-rights.jpg`,
    alt: 'Housing rights track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'eu-legislation',
    name: 'EU Legislation',
    category: 'legal',
    path: `${TRACKS_BASE}/eu-legislation.jpg`,
    alt: 'EU legislation track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'taxes-taxation',
    name: 'Taxes & Taxation',
    category: 'legal',
    path: `${TRACKS_BASE}/taxes-taxation.jpg`,
    alt: 'Taxes and taxation track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'social-security',
    name: 'Social Security',
    category: 'legal',
    path: `${TRACKS_BASE}/social-security.jpg`,
    alt: 'Social security track cover',
    width: 800,
    height: 600,
  },
];

/**
 * Lifestyle and wellbeing tracks
 */
export const LIFESTYLE_TRACKS: TrackImageDefinition[] = [
  {
    id: 'healthcare-sns',
    name: 'Healthcare & SNS',
    category: 'lifestyle',
    path: `${TRACKS_BASE}/healthcare-sns.jpg`,
    alt: 'Healthcare and SNS track cover',
    width: 800,
    height: 600,
  },
];

/**
 * Professional and work-related tracks
 */
export const PROFESSIONAL_TRACKS: TrackImageDefinition[] = [
  {
    id: 'work-employment',
    name: 'Work & Employment',
    category: 'professional',
    path: `${TRACKS_BASE}/work-employment.jpg`,
    alt: 'Work and employment track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'corporate-environment',
    name: 'Corporate Environment',
    category: 'professional',
    path: `${TRACKS_BASE}/corporate-environment.jpg`,
    alt: 'Corporate environment track cover',
    width: 800,
    height: 600,
  },
];

/**
 * Technology and skills tracks
 */
export const TECHNOLOGY_TRACKS: TrackImageDefinition[] = [
  {
    id: 'agile-methodologies',
    name: 'Agile Methodologies',
    category: 'technology',
    path: `${TRACKS_BASE}/agile-methodologies.jpg`,
    alt: 'Agile methodologies track cover',
    width: 800,
    height: 600,
  },
  {
    id: 'java-programming',
    name: 'Java Programming',
    category: 'technology',
    path: `${TRACKS_BASE}/java-programming.jpg`,
    alt: 'Java programming track cover',
    width: 800,
    height: 600,
  },
];

/**
 * All tracks combined
 */
export const ALL_TRACKS: TrackImageDefinition[] = [
  ...CULTURE_TRACKS,
  ...LEGAL_TRACKS,
  ...LIFESTYLE_TRACKS,
  ...PROFESSIONAL_TRACKS,
  ...TECHNOLOGY_TRACKS,
];

/**
 * Track image lookup by ID
 */
export const TRACK_IMAGES_BY_ID: Record<string, TrackImageDefinition> = ALL_TRACKS.reduce(
  (acc, track) => {
    acc[track.id] = track;
    return acc;
  },
  {} as Record<string, TrackImageDefinition>
);

/**
 * Get track image by ID
 */
export function getTrackImage(trackId: string): string | undefined {
  return TRACK_IMAGES_BY_ID[trackId]?.path;
}

/**
 * Get track image asset by ID
 */
export function getTrackImageAsset(trackId: string): TrackImageDefinition | undefined {
  return TRACK_IMAGES_BY_ID[trackId];
}

/**
 * Get tracks by category
 */
export function getTracksByCategory(category: TrackCategory): TrackImageDefinition[] {
  return ALL_TRACKS.filter((track) => track.category === category);
}
